/**
 * Galaxy map overlay registry (M4 plan I7, spec §12.5, seam S-M3-7). M3's map took `overlay: 'region' | 'danger'`;
 * this generalizes it additively: M3's two stay built-in layers (the model still colours them from
 * `reach-maps.json`, so they render exactly as before), and other modules add `MapOverlayLayer`s with
 * `registerMapOverlay` (M4's economy layers, later M5's). The map's overlay selector lists `mapOverlays()`.
 *
 * The registry also carries two small hooks the map view drives:
 * - `setMapOpen` / `onMapOpenChange`: layer providers fetch their data only while a map is open.
 * - `setStationInfoSource`: the system map's station details ask it for extra lines (M4's station-info card).
 *
 * UI events only (registration, open and close, rebuilds); nothing here runs per frame.
 */
import type { ReachMapsConfig } from './config'

export interface MapOverlayNodeStyle {
  /** CSS colour: `#rrggbb` or `rgb(r, g, b)`. */
  color: string
  /** Multiplier of the standard node radius (1 = as M3 draws it). */
  size: number
  /** A short glyph after the system's label (colour-blind-safe cue). */
  badge?: string
}

export interface MapOverlayEdgeStyle {
  /** CSS colour: `#rrggbb` or `rgb(r, g, b)`. */
  color: string
  /** Relative width: 1 = the thin relay line; above 1 the map draws a tube that thick. */
  width: number
  dashed?: boolean
}

/** A picker a layer shows under its legend (for example the ware of "Price of…"). */
export interface MapOverlayPicker {
  label: string
  options: readonly { id: string; label: string }[]
  readonly value: string
  select(id: string): void
}

/** Spec §12.5, plus the optional picker. `node` / `edge` null = draw the plain style. */
export interface MapOverlayLayer {
  id: string
  label: string
  legend: readonly { label: string; color: string }[]
  node(systemId: string): MapOverlayNodeStyle | null
  edge(relayId: string): MapOverlayEdgeStyle | null
  tooltip(systemId: string): readonly string[]
  picker?: MapOverlayPicker | null
}

/** M3's own overlays: the model colours them from `reach-maps.json`. */
export type BuiltinOverlay = 'region' | 'danger'
export const BUILTIN_OVERLAYS: readonly BuiltinOverlay[] = ['region', 'danger']

export function isBuiltinOverlay(id: string): id is BuiltinOverlay {
  return id === 'region' || id === 'danger'
}

const BUILTIN_LABELS: Record<BuiltinOverlay, string> = {
  region: 'Regions',
  danger: 'Danger',
}
const none = (): null => null
const noLines = (): readonly string[] => []

/** A built-in layer: its colours come from the model, so `node` and `edge` are null. The legend reads the config. */
export function builtinOverlay(id: BuiltinOverlay, cfg: ReachMapsConfig, regionNames: Readonly<Record<string, string>>): MapOverlayLayer {
  const colors = cfg.galaxy.colors
  const legend =
    id === 'region'
      ? (Object.keys(colors.region) as (keyof typeof colors.region)[]).map((r) => ({ label: regionNames[r] ?? r, color: colors.region[r] }))
      : colors.danger.map(([threshold, color]) => ({
          label: `danger ≥ ${threshold}`,
          color,
        }))
  return {
    id,
    label: BUILTIN_LABELS[id],
    legend,
    node: none,
    edge: none,
    tooltip: noLines,
    picker: null,
  }
}

const layers: MapOverlayLayer[] = []
let version = 0

/** Adds (or replaces, by id) a layer; returns its removal. Built-in ids are refused. */
export function registerMapOverlay(layer: MapOverlayLayer): () => void {
  if (isBuiltinOverlay(layer.id)) throw new Error(`Map overlay "${layer.id}" is built in`)
  const at = layers.findIndex((l) => l.id === layer.id)
  if (at >= 0) layers[at] = layer
  else layers.push(layer)
  version++
  return () => {
    const i = layers.indexOf(layer)
    if (i >= 0) {
      layers.splice(i, 1)
      version++
    }
  }
}

/** The registered (non-built-in) layers, in registration order. */
export function mapOverlays(): readonly MapOverlayLayer[] {
  return layers
}

export function mapOverlay(id: string): MapOverlayLayer | undefined {
  return layers.find((l) => l.id === id)
}

/** Bumped whenever a layer is added or removed or its data changed (`notifyMapOverlays`): the open map rebuilds. */
export function mapOverlayVersion(): number {
  return version
}

/** A layer's data changed (a new economy view arrived, the picker moved). */
export function notifyMapOverlays(): void {
  version++
}

type OpenListener = (open: boolean) => void
const openListeners = new Set<OpenListener>()
let mapOpen = false

/** The map view calls this on show and hide. */
export function setMapOpen(open: boolean): void {
  if (open === mapOpen) return
  mapOpen = open
  for (const fn of [...openListeners]) fn(open)
}

export function isMapOpen(): boolean {
  return mapOpen
}

/** Subscribes to map open / close; called at once with the current state. Returns the unsubscribe. */
export function onMapOpenChange(fn: OpenListener): () => void {
  openListeners.add(fn)
  fn(mapOpen)
  return () => {
    openListeners.delete(fn)
  }
}

/** Extra details for a station selected on the system map. */
export interface StationInfoList {
  heading: string
  items: readonly string[]
}
export type StationInfoSource = (stationId: string) => Promise<readonly StationInfoList[]>

let stationInfo: StationInfoSource | null = null

/** Sets (or clears, with null) the station-info source; returns a clear that only clears this source. */
export function setStationInfoSource(src: StationInfoSource | null): () => void {
  stationInfo = src
  return () => {
    if (stationInfo === src) stationInfo = null
  }
}

export function stationInfoSource(): StationInfoSource | null {
  return stationInfo
}

/** Test helper: drops every layer, listener and source. */
export function resetMapOverlays(): void {
  layers.length = 0
  openListeners.clear()
  mapOpen = false
  stationInfo = null
  version++
}

const RGB = /^rgb\((\d+(?:\.\d+)?), ?(\d+(?:\.\d+)?), ?(\d+(?:\.\d+)?)\)$/

/** `#rrggbb` or `rgb(r, g, b)` → 0xRRGGBB (NaN-free: an unknown string is 0x808080 grey). */
export function cssColorNumber(css: string): number {
  if (/^#[0-9A-Fa-f]{6}$/.test(css)) return Number.parseInt(css.slice(1), 16)
  const m = RGB.exec(css)
  if (m === null) return 0x808080
  const ch = (s: string): number => Math.max(0, Math.min(255, Math.round(Number(s))))
  return (ch(m[1]) << 16) | (ch(m[2]) << 8) | ch(m[3])
}
